import { CommonUIError } from '@afterpay/types'
import clsx from 'clsx'
import { createMask, MaskedDynamic } from 'imask'
import type { CountryCode } from 'libphonenumber-js'
import { getCountryCallingCode } from 'libphonenumber-js'
import React, { FocusEvent, ForwardedRef, forwardRef, isValidElement, KeyboardEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react'

import { Dropdown } from '../Dropdown'
import { Icon } from '../Icon'
import { InputSizes } from '../Input'
import inputStyles from '../Input/style.module.scss'
import { DesignLanguage, useIsDesignLanguage, useThemeClass } from '../Theme'
import { Span, Text } from '../Typography'
import { noop } from '../utils/function'
import { KeyCodes } from '../utils/keyboard'
import { getCountryEmoji, getFirstNonEmptyString } from '../utils/string'
import { AllowCountriesConfig, DEFAULT_CONFIG, LEADING_ZERO_FORMAT, PHONE_FORMAT_CHAR_ALLOWED } from './constants'
import { isDigitInput, transformedValue, TransformedValueResponse } from './helper'
import styles from './style.module.scss'

export interface CallbackExtraFieldsType {
  isMobileInputMode: boolean
  countryRawValue: string
  isMobileNumberValid: boolean
  rawValue?: string
}

type OmitProps = 'size' | 'onChange' | 'onBlur' | 'className' | 'style' | 'placeholder' | 'value' | 'defaultValue' | 'type'
export interface InputEmailPhoneProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, OmitProps>, CommonUIError {
  enableInputPhoneMode?: boolean
  value?: string
  defaultValue?: string
  label?: string
  autoSelectOnFocus?: boolean
  allowedCountries?: AllowCountriesConfig[]
  selectedCountry?: CountryCode
  onChange?: (e: React.ChangeEvent<HTMLInputElement>, others: CallbackExtraFieldsType) => void
  onCountryChange?: (data: CountryCode) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>, others: CallbackExtraFieldsType) => void
  size?: InputSizes
  testNameSpace?: string
  id?: string
  singleCountryShowEmoji?: boolean // show country emoji even when there is only one allowed country
  type?: 'email' | 'hidden'
}

export const InputEmailPhone = forwardRef((props: InputEmailPhoneProps, ref: ForwardedRef<HTMLInputElement>) => {
  const {
    disabled,
    error,
    singleCountryShowEmoji,
    readOnly,
    id,
    errorText,
    testNameSpace,
    defaultValue = '',
    enableInputPhoneMode = true,
    autoSelectOnFocus = true,
    allowedCountries = DEFAULT_CONFIG,
    selectedCountry = 'AU',
    label = '',
    onChange = noop,
    onCountryChange = noop,
    onBlur = noop,
    size = 'Large',
    name,
    type = 'email',
    ...otherProps
  } = props

  const themeClass = useThemeClass()
  const isArcadeDesign = useIsDesignLanguage(DesignLanguage.Arcade)
  const initialInputMode = typeof props.value === 'undefined' ? isDigitInput(defaultValue, false) : false
  const [isMobileInputMode, setMobileInputMode] = useState(enableInputPhoneMode ? initialInputMode : false)
  const shouldShowDropdown = isMobileInputMode && allowedCountries.length > 1
  const selectedCountryIndex = allowedCountries.findIndex((c) => c.countryCode === selectedCountry)
  const isSelectedCountryValid = selectedCountryIndex >= 0
  const [country, setCountry] = useState<CountryCode>(isSelectedCountryValid ? selectedCountry : allowedCountries[0].countryCode)
  const useMask = useMemo(() => {
    return createMask({
      mask: PHONE_FORMAT_CHAR_ALLOWED[country],
      dispatch: (appended: string, dynamicMasked: MaskedDynamic) => {
        const currentValue = `${dynamicMasked.unmaskedValue}${appended}`
        // @ts-expect-error compiledMasks is not in MaskedDynamic type but exists at runtime
        return dynamicMasked.compiledMasks.find((mask) => currentValue.indexOf(mask.startsWith) === 0)
      },
    })
  }, [country])
  const value = typeof props.value === 'undefined' ? defaultValue : props.value
  const [inputValue, setInputValue] = useState(isMobileInputMode ? useMask.resolve(value) : value)
  const [inputRawValue, setInputRawValue] = useState(value)
  const [isFocus, setFocus] = useState(false)
  const [isFilled, setIsFilled] = useState(value.length > 0)
  const componentRef = useRef<HTMLDivElement>(null)
  const [focusIndex, setFocusIndex] = useState(isSelectedCountryValid ? selectedCountryIndex : 0)
  const [showMenu, setShowMenu] = useState(false)
  const countryRequiredLeadingZero = LEADING_ZERO_FORMAT[country] ?? LEADING_ZERO_FORMAT.AU
  const menuId = `${id}-input-email-phone-countries-menu`
  const hasTestId = typeof testNameSpace === 'string' && testNameSpace.length > 0
  const errorId = `${getFirstNonEmptyString(id, name, testNameSpace)}-error`

  const componentClassNames = clsx(
    inputStyles.component,
    isFocus && inputStyles.componentActive,
    inputStyles[`modSize${size}` as const],
    disabled && inputStyles.modDisabled,
    isFilled && inputStyles.modFilled,
    error && inputStyles.modError,
    error && styles.modError,
    isFocus && inputStyles.modFocus,
    (shouldShowDropdown || singleCountryShowEmoji) && inputStyles.disabledLeftBorderRadius,
    (shouldShowDropdown || singleCountryShowEmoji) && styles.disabledLeftBorderRadius,
    inputStyles[themeClass]
  )
  const componentStyles = clsx(styles.component, showMenu && styles.expanded, styles[`${themeClass}ComponentWrapper`], styles[themeClass])
  const inputComponentStyle = clsx(inputStyles.input, styles.input)
  const labelComponentStyle = clsx(inputStyles.label, styles.label)
  const inputWrapperStyles = clsx(styles.inputWrapper, (shouldShowDropdown || singleCountryShowEmoji) && styles.paddedInputWrapper)
  const countriesButtonStyles = clsx(styles.countryButton, disabled && styles.disabled, readOnly && styles.readOnly, error && styles.error)
  const countriesIconStyles = clsx(styles.countryIconWrapper, error && styles.error)

  const handleInputValueChange = (value: string): TransformedValueResponse => {
    const res = transformedValue(useMask, value, enableInputPhoneMode, isMobileInputMode, country, countryRequiredLeadingZero)
    const { viewValue, rawValue, isMobileMode } = res
    if (typeof props.value === 'undefined') {
      setInputValue(viewValue)
      if (enableInputPhoneMode) {
        setMobileInputMode(isMobileMode)
      }
    }
    setInputRawValue(rawValue)
    setIsFilled(rawValue.length > 0)
    return res
  }

  const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>): void => {
    const { isMobileMode, countryRawValue, rawValue, isMobileNumberValid } = handleInputValueChange(e.target.value)
    setFocus(false)
    onBlur(e, { isMobileInputMode: isMobileMode, countryRawValue, rawValue, isMobileNumberValid })
  }

  const handleCountriesClick = (): void => {
    if (!disabled && !readOnly) {
      setShowMenu(!showMenu)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>): void => {
    if (disabled ?? readOnly) {
      return
    }

    const { key } = e
    if (key === KeyCodes.Enter) {
      e.preventDefault()
      if (showMenu) {
        setCountry(allowedCountries[focusIndex].countryCode)
        onCountryChange(allowedCountries[focusIndex].countryCode)
      }
      setShowMenu(!showMenu)
    }

    if (key === KeyCodes.ArrowDown) {
      e.preventDefault()
      setFocusIndex(focusIndex >= allowedCountries.length - 1 ? 0 : focusIndex + 1)
    }
    if (key === KeyCodes.ArrowUp) {
      e.preventDefault()
      setFocusIndex(focusIndex === 0 ? allowedCountries.length - 1 : focusIndex - 1)
    }
    if (key === KeyCodes.Escape) {
      e.preventDefault()
      setShowMenu(false)
    }
  }

  const handleEscape = (e: FocusEvent<HTMLButtonElement> & { relatedTarget: HTMLElement | null }): void => {
    if (e.relatedTarget?.parentElement?.id !== menuId) {
      setShowMenu(false)
    }
  }

  const handleCountryClick = (countryCode: CountryCode, index: number): void => {
    setFocusIndex(index)
    setCountry(countryCode)
    setShowMenu(false)

    onCountryChange(countryCode)
  }

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { isMobileMode, countryRawValue, rawValue, isMobileNumberValid } = handleInputValueChange(event.target.value)
    onChange(event, { isMobileInputMode: isMobileMode, countryRawValue, rawValue, isMobileNumberValid })
  }

  const onFocusHandler = (event: React.FocusEvent<HTMLInputElement>): void => {
    if (autoSelectOnFocus) {
      event.target.select()
    }
    setFocus(true)
  }

  useEffect(() => {
    if (showMenu) {
      componentRef?.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [showMenu])

  // PAYL-17443: Chrome autofill not hiding placeholder text
  // ref: https://kishoreconnect.com/javascript-and-google-chrome-autofill
  useEffect(() => {
    setTimeout(() => {
      let autofilledInput
      try {
        autofilledInput = document.querySelector('input::-webkit-autofill')
      } catch {
        // querySelector may throw in some browsers for non-standard selectors
      }
      if (autofilledInput != null) {
        setIsFilled(true)
      }
    }, 500)
  }, [])

  useEffect(() => {
    if (typeof props.value !== 'undefined') {
      const rawValue = props.value
      const isMobileMode = isDigitInput(rawValue, isMobileInputMode)
      const viewValue = enableInputPhoneMode && isMobileMode ? useMask.resolve(rawValue) : rawValue

      setInputValue(viewValue)
      if (enableInputPhoneMode) {
        setMobileInputMode(isMobileMode)
      }
    }
  }, [props.value])

  if (type === 'hidden') {
    const hiddenValue = typeof props.value === 'undefined' ? defaultValue : props.value
    return <input {...otherProps} {...(hasTestId && { 'data-testid': `${testNameSpace}-input` })} type='hidden' name={name} value={hiddenValue} ref={ref} />
  }

  const renderErrorInfo = (): ReactNode => {
    if (error && ((typeof errorText === 'string' && errorText.length > 0) || isValidElement(errorText))) {
      return (
        <Span className={styles.errorText} color='Fire' size='XS' id={errorId} {...(hasTestId && { 'data-testid': `${testNameSpace}-error` })}>
          {errorText}
        </Span>
      )
    }

    return null
  }

  const renderDropdownButton = (): ReactNode => {
    return shouldShowDropdown ? (
      <button
        tabIndex={disabled ? -1 : 0}
        className={countriesButtonStyles}
        onClick={handleCountriesClick}
        onKeyDown={handleKeyDown}
        onBlur={handleEscape}
        aria-label={`${allowedCountries[focusIndex].name} ${label}`}
        type='button'
        {...(hasTestId && { 'data-testid': `${testNameSpace}-countries-toggle` })}
      >
        {getCountryEmoji(country)}{' '}
        <span className={styles.iconWrapper}>
          <Icon.CaretDown size='ExtraSmall' title='Select a country' />
        </span>
      </button>
    ) : (
      singleCountryShowEmoji && (
        <div className={countriesIconStyles}>
          <span className={styles.countryIcon}>{getCountryEmoji(country)}</span>
        </div>
      )
    )
  }

  const renderDropdownMenu = (): ReactNode => {
    if (showMenu) {
      return (
        <div className={styles.menuWrapper}>
          <Dropdown.Menu
            key={menuId}
            id={menuId}
            aria-activedescendant={`input-email-phone-countries-menu_${focusIndex}`}
            {...(hasTestId && { testNameSpace: `${testNameSpace}-countries` })}
          >
            {allowedCountries.map((allowedCountry, index) => {
              const { countryCode, name } = allowedCountry
              return (
                <Dropdown.MenuItem
                  key={`${menuId}-${countryCode as string}`}
                  focus={focusIndex === index}
                  onClick={() => handleCountryClick(countryCode, index)}
                  {...(hasTestId && { 'data-testid': `${testNameSpace}-country-name-${countryCode.toLowerCase()}` })}
                >
                  <span className={styles.flagEmoji}>{getCountryEmoji(countryCode)}</span>
                  <span className={styles.countryName}>{name}</span>
                  <Text renderAs='span' color='Gray40'>
                    +{getCountryCallingCode(countryCode)}
                  </Text>
                </Dropdown.MenuItem>
              )
            })}
          </Dropdown.Menu>
        </div>
      )
    }

    return null
  }

  const renderInput = (): ReactNode => (
    <div className={inputWrapperStyles}>
      <div className={componentClassNames} {...(hasTestId && { 'data-testid': `${testNameSpace}-wrapper` })}>
        <label className={labelComponentStyle} {...(hasTestId && { 'data-testid': `${testNameSpace}-label` })}>
          {label}
        </label>
        <div className={clsx(inputStyles.inputWrapper, inputStyles.hasIconPositionRight)}>
          <input
            {...otherProps}
            {...(hasTestId && { 'data-testid': `${testNameSpace}-input` })}
            className={inputComponentStyle}
            disabled={disabled}
            onChange={onChangeHandler}
            type={type}
            value={inputValue}
            aria-label={label}
            data-rawdata={inputRawValue}
            ref={ref}
            onFocus={onFocusHandler}
            onBlur={onBlurHandler}
            {...(error && { 'aria-describedby': errorId })}
          />
          {isArcadeDesign && error && (
            <span className={inputStyles.helper} data-testid='input-email-phone-error-icon'>
              <Icon.Warning size='Small' color='Fire' />
            </span>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div className={componentStyles} ref={componentRef}>
      <div className={styles.componentWrapper}>
        {renderDropdownButton()}
        {renderInput()}
      </div>
      {renderErrorInfo()}
      {renderDropdownMenu()}
    </div>
  )
})
